import React from "react";
import { Star } from "lucide-react";
import Seo, { localBusinessSchema } from "../components/site/Seo";
import Breadcrumbs from "../components/site/Breadcrumbs";
import PageHero from "../components/site/PageHero";
import Reveal from "../components/site/Reveal";
import FinalCTA from "../components/site/FinalCTA";
import { REVIEWS, IMG, REVIEW_RATING, REVIEW_COUNT } from "../lib/site";
import { useGoogleReviews } from "../lib/useGoogleReviews";

const Reviews = () => {
  const g = useGoogleReviews();
  const live = g.configured && g.reviews && g.reviews.length > 0;
  const cards = live
    ? g.reviews.map((r) => ({ name: r.author, meta: r.relative_time, text: r.text, rating: r.rating || 5 }))
    : REVIEWS.map((r) => ({ name: r.name, meta: r.city, text: r.text, rating: 5 }));
  const rating = (g.configured && g.rating ? g.rating : REVIEW_RATING).toFixed(1);
  const countLabel = `${g.configured && g.total ? g.total : REVIEW_COUNT}`;

  return (
    <div>
      <Seo
        title="Reviews | 5.0 Stars on Google | PRYME EPOXY"
        description="Read what Northern Arizona homeowners say about PRYME EPOXY. Rated 5.0 stars on Google for communication, craftsmanship, and never cutting corners."
        path="/reviews"
        jsonLd={localBusinessSchema()}
      />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Reviews", path: "/reviews" }]} />

      <PageHero
        overline="Customer Reviews"
        title="Rated 5.0 Stars by Our Neighbors"
        subtitle="Real Google reviews from homeowners and businesses across Northern Arizona. Here's what it's like to work with Tyler and the PRYME EPOXY crew."
        image={IMG.metallic}
      />

      <section className="py-10 bg-zinc-950 border-y border-zinc-900">
        <div className="max-w-7xl mx-auto px-5 lg:px-12 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <div className="flex text-yellow-400">{[...Array(5)].map((_, i) => <Star key={i} className="w-7 h-7 fill-current" />)}</div>
          <p className="text-white text-lg" data-testid="reviews-rating-summary">
            <strong>{rating}</strong> average rating • <strong>{countLabel}</strong> Google reviews
          </p>
          {g.url && (
            <a href={g.url} target="_blank" rel="noopener noreferrer" data-testid="reviews-google-link" className="text-[#C69453] font-semibold hover:underline">
              {countLabel ? `Read all ${countLabel} on Google →` : "See our Google reviews →"}
            </a>
          )}
        </div>
      </section>

      <section className="py-16 md:py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 lg:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((r, i) => (
            <Reveal key={i} delay={(i % 3) * 0.06}>
              <div className="border border-zinc-800 bg-[#141414] p-7 h-full flex flex-col" data-testid="review-card">
                <div className="flex text-yellow-400 mb-4">{[...Array(r.rating)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" />)}</div>
                <p className="text-zinc-300 leading-relaxed flex-grow line-clamp-[10]">&ldquo;{r.text}&rdquo;</p>
                <div className="mt-5 pt-4 border-t border-zinc-900 flex items-center justify-between">
                  <div>
                    <p className="text-white font-bold">{r.name}</p>
                    <p className="text-zinc-500 text-sm">{r.meta}</p>
                  </div>
                  {live && <span className="text-zinc-600 text-xs uppercase tracking-widest">Google</span>}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <FinalCTA heading="Join Our Growing List of Happy Northern Arizona Homeowners" />
    </div>
  );
};

export default Reviews;
